import { useCommissionAccess } from "@/hooks/useCommissionAccess";
import { canApproveCommission, commissionCopy } from "@/lib/app/commission-access";
import {
  Check,
  EyeOff,
  MessageCircle,
  PiggyBank,
  Settings,
  Trash2Icon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { isApproved, isPaid, isUnapproved, isIgnored } from "@/lib/app";
import { CommissionComments } from "./CommissionComments";
import { CommissionStatus, NestedCommission } from "@revelate/types";

interface CommissionActionsProps {
  commissions: NestedCommission[];
  updateCommissions: (
    previousStatus: CommissionStatus,
    newStatus: CommissionStatus,
    filterByCommission?: NestedCommission
  ) => void;
  isLoading?: boolean;
}

export const CommissionActions = ({
  commissions,
  updateCommissions,
  isLoading = false,
}: CommissionActionsProps) => {
  const { data: access } = useCommissionAccess();
  if (!commissions.length) return null;
  const canFirst = commissions.every(c => canApproveCommission(access, c.user_id));
  const canFinal = commissions.every(c => canApproveCommission(access, c.user_id, true));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="icon-sm"
          variant={
            isUnapproved(commissions) || isApproved(commissions)
              ? "default"
              : "outline"
          }
          disabled={isLoading}
        >
          {isUnapproved(commissions) ? (
            <Check className="h-5 w-5" />
          ) : isApproved(commissions) ? (
            <PiggyBank className="h-5 w-5" />
          ) : isPaid(commissions) ? (
            <Settings className="h-5 w-5" />
          ) : isIgnored(commissions) ? (
            <EyeOff className="h-5 w-5" />
          ) : (
            <Settings className="h-5 w-5" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {/* Primary actions based on current state */}
        {canFirst && isUnapproved(commissions) && (
          <>
            <DropdownMenuItem
              className="font-bold cursor-pointer"
              onClick={() => {
                if (commissions.length > 0) {
                  updateCommissions("unapproved", "approved", commissions[0]);
                }
              }}
            >
              <Check className="mr-2 h-4 w-4" />
              {commissionCopy.approve}
            </DropdownMenuItem>
          </>
        )}

        {canFinal && isApproved(commissions) && (
          <DropdownMenuItem
            className="font-bold cursor-pointer"
            onClick={() => {
              if (commissions.length > 0) {
                updateCommissions("approved", "paid", commissions[0]);
              }
            }}
          >
            <PiggyBank className="mr-2 h-4 w-4" />
            {commissionCopy.finalApprove}
          </DropdownMenuItem>
        )}

        {canFinal && isPaid(commissions) && (
          <DropdownMenuItem
            className="font-bold cursor-pointer"
            onClick={() => {
              if (commissions.length > 0) {
                updateCommissions("paid", "unapproved", commissions[0]);
              }
            }}
          >
            <Trash2Icon className="mr-2 h-4 w-4" />
            {commissionCopy.reopen}
          </DropdownMenuItem>
        )}

        {canFinal && isIgnored(commissions) && (
          <DropdownMenuItem
            className="font-bold cursor-pointer"
            onClick={() => {
              if (commissions.length > 0) {
                updateCommissions("ignored", "unapproved", commissions[0]);
              }
            }}
          >
            <Trash2Icon className="mr-2 h-4 w-4" />
            {commissionCopy.reopen}
          </DropdownMenuItem>
        )}

        {!isPaid(commissions) && !isIgnored(commissions) && (
          <DropdownMenuSeparator />
        )}

        {/* Add Comment option for all commission states */}
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={(e) => e.preventDefault()}>
          <MessageCircle className="mr-2 h-4 w-4" />
          <div className="flex-1">
            <CommissionComments
              commission={commissions[0]}
            />
          </div>
        </DropdownMenuItem>

        {canFinal && !isUnapproved(commissions) &&
          !isPaid(commissions) &&
          !isIgnored(commissions) && (
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => {
                if (commissions.length > 0) {
                  updateCommissions(
                    commissions[0].status as CommissionStatus,
                    "unapproved",
                    commissions[0]
                  );
                }
              }}
            >
              <Trash2Icon className="mr-2 h-4 w-4" />
              {commissionCopy.reopen}
            </DropdownMenuItem>
          )}

        {/* isUnapproved(commissions) && (
          <DropdownMenuItem
            onClick={() => {
              if (commissions.length > 0) {
                updateCommissions(
                  commissions[0].status as CommissionStatus,
                  "ignored",
                  commissions[0]
                );
              }
            }}
          >
            <EyeOff className="mr-2 h-4 w-4" />
            Ignore
          </DropdownMenuItem>
        ) */}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
